import { api } from './api';

// Where notifications stand on this device:
//   unavailable — the server has no VAPID keys, so there is nothing to offer
//   unsupported — this browser cannot receive Web Push at all, or there is no
//                 service worker (as under the Vite dev server)
//   install     — iPhone/iPad in a Safari tab: push needs the home-screen app
//   blocked     — the person said no, and only browser settings can undo it
//   off / on    — the toggle
export type PushStatus = 'unavailable' | 'unsupported' | 'install' | 'blocked' | 'off' | 'on';

let publicKey: Promise<string> | null = null;
const getKey = () => (publicKey ??= api.pushKey().then((r) => r.publicKey, () => ''));

function isIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function isStandalone(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone === true;
}

function supported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

async function currentSubscription(): Promise<PushSubscription | null> {
  const reg = await navigator.serviceWorker.getRegistration();
  return (await reg?.pushManager.getSubscription()) ?? null;
}

export async function pushStatus(): Promise<PushStatus> {
  if (!(await getKey())) return 'unavailable';
  if (!supported()) return isIOS() && !isStandalone() ? 'install' : 'unsupported';
  if (!(await navigator.serviceWorker.getRegistration())) return 'unsupported';
  if (Notification.permission === 'denied') return 'blocked';
  if (Notification.permission !== 'granted') return 'off';
  return (await currentSubscription()) ? 'on' : 'off';
}

/** Asks for permission (so call it from a tap) and registers this browser. */
export async function enablePush(): Promise<PushStatus> {
  const key = await getKey();
  if (!key || !supported()) return pushStatus();
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission === 'denied' ? 'blocked' : 'off';

  // Not .ready: that never settles when no service worker is registered.
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return 'unsupported';
  const sub =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToBytes(key) }));
  await api.pushSubscribe(sub.toJSON());
  return 'on';
}

export async function disablePush(): Promise<PushStatus> {
  const sub = supported() ? await currentSubscription() : null;
  if (sub) {
    await api.pushUnsubscribe(sub.endpoint).catch(() => undefined);
    await sub.unsubscribe().catch(() => undefined);
  }
  return pushStatus();
}

/**
 * Re-sends this browser's subscription on start-up. It keeps the server right
 * if the subscription was rotated while the app was closed, or if a different
 * person has since signed in on this device.
 */
export async function resyncPush(): Promise<void> {
  if (!supported() || Notification.permission !== 'granted' || !(await getKey())) return;
  const sub = await currentSubscription();
  if (sub) await api.pushSubscribe(sub.toJSON()).catch(() => undefined);
}

function urlBase64ToBytes(b64: string): Uint8Array<ArrayBuffer> {
  const padded = (b64 + '='.repeat((4 - (b64.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(padded);
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}
